import React from "react";
import { Link } from "react-router-dom";
import { Truck, Mail, Phone, MapPin, Shield, Facebook } from "lucide-react";
import { useLang } from "@/lib/i18n";

const Footer = () => {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-[#050509] border-t border-white/10 pt-20 pb-10 mt-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden">
                <img src="/wasz-logo.png" alt="Wasz.nl logo" className="w-full h-full object-contain" />
              </div>
              <div className="leading-tight">
                <div className="text-white font-extrabold tracking-tight text-base">
                  Wasz<span className="text-[#00D4FF]">.nl</span>
                </div>
                <div className="text-[10px] text-white/40 uppercase tracking-[0.18em] font-medium">
                  Mobile Truckwash
                </div>
              </div>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed max-w-md">{t("footer.tagline")}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-[#10B981] border border-[#10B981]/30 bg-[#10B981]/5 rounded-md px-3 py-1.5 font-medium">
              <Shield className="w-3.5 h-3.5" /> {t("footer.certified")}
            </div>

            <ul className="mt-8 space-y-3 text-sm text-white/60">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                <div>
                  <div>{t("footer.postal_label")}: Scharstraat 25, 1317 PL Almere</div>
                  <div className="text-white/40 text-xs mt-1 italic">{t("footer.mobile_note")}</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                <a href="tel:0365255021" data-testid="footer-phone" className="hover:text-white">
                  036 525 5021
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                <a href="mailto:info@wasz.nl" data-testid="footer-email" className="hover:text-white">
                  info@wasz.nl
                </a>
              </li>
            </ul>

            <div className="mt-6 flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/35 font-semibold">
                {lang === "nl" ? "Volg ons" : "Follow us"}
              </span>
              <a
                href="https://www.facebook.com/Wasz.nl"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                aria-label="Wasz.nl op Facebook"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-white/10 text-white/55 hover:text-[#00D4FF] hover:border-[#00D4FF]/40 transition-colors"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold mb-5 tracking-wide">{t("footer.services")}</h4>
            <ul className="space-y-3 text-sm text-white/55">
              <li><Link to="/mobiele-truckwash" className="hover:text-[#00D4FF]">{t("nav.mobile_truckwash")}</Link></li>
              <li><Link to="/mobiele-truckwash-nederland" data-testid="footer-mobiele-truckwash-nl" className="hover:text-[#00D4FF]">{lang === "nl" ? "Mobiele truckwash Nederland" : "Mobile truckwash Netherlands"}</Link></li>
              <li><Link to="/vrachtwagenreiniging" className="hover:text-[#00D4FF]">{t("nav.truck_cleaning")}</Link></li>
              <li><Link to="/fleet-cleaning" className="hover:text-[#00D4FF]">{t("nav.fleet_cleaning")}</Link></li>
              <li><Link to="/wagenpark-reiniging" className="hover:text-[#00D4FF]">{t("nav.wagenpark")}</Link></li>
              <li><Link to="/wagenparkreiniging" data-testid="footer-wagenparkreiniging" className="hover:text-[#00D4FF]">{lang === "nl" ? "Wagenparkreiniging" : "Fleet cleaning service"}</Link></li>
              <li><Link to="/haccp-certificaten" className="hover:text-[#00D4FF]">{t("nav.haccp")}</Link></li>
              <li><Link to="/waszbase" className="hover:text-[#00D4FF]">{t("nav.waszbase")}</Link></li>
              <li><Link to="/digitale-wagenparkadministratie" data-testid="footer-digitale-admin" className="hover:text-[#00D4FF]">{lang === "nl" ? "Digitale wagenparkadministratie" : "Digital fleet administration"}</Link></li>
              <li><Link to="/reinigingsregistratie-transport" data-testid="footer-reinigingsregistratie" className="hover:text-[#00D4FF]">{lang === "nl" ? "Reinigingsregistratie transport" : "Cleaning records transport"}</Link></li>
              <li><Link to="/certificaatbeheer-wagenpark" data-testid="footer-certificaatbeheer" className="hover:text-[#00D4FF]">{lang === "nl" ? "Certificaatbeheer wagenpark" : "Fleet certificate management"}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold mb-5 tracking-wide">{t("footer.locations")}</h4>
            <ul className="space-y-3 text-sm text-white/55">
              <li><Link to="/truckwash-almere" className="hover:text-[#00D4FF]">Truckwash Almere</Link></li>
              <li><Link to="/truckwash-amsterdam" className="hover:text-[#00D4FF]">Truckwash Amsterdam</Link></li>
              <li><Link to="/truckwash-den-haag" className="hover:text-[#00D4FF]">Truckwash Den Haag</Link></li>
              <li><Link to="/truckwash-rotterdam" className="hover:text-[#00D4FF]">Truckwash Rotterdam</Link></li>
              <li><Link to="/truckwash-utrecht" className="hover:text-[#00D4FF]">Truckwash Utrecht</Link></li>
              <li><Link to="/truckwash-amersfoort" className="hover:text-[#00D4FF]">Truckwash Amersfoort</Link></li>
              <li><Link to="/truckwash-alkmaar" className="hover:text-[#00D4FF]">Truckwash Alkmaar</Link></li>
              <li><Link to="/truckwash-alphen-aan-den-rijn" className="hover:text-[#00D4FF]">Truckwash Alphen aan den Rijn</Link></li>
              <li><Link to="/truckwash-barneveld" className="hover:text-[#00D4FF]">Truckwash Barneveld</Link></li>
              <li><Link to="/truckwash-breda" className="hover:text-[#00D4FF]">Truckwash Breda</Link></li>
              <li><Link to="/truckwash-waddinxveen" className="hover:text-[#00D4FF]">Truckwash Waddinxveen</Link></li>
              <li><Link to="/truckwash-zeewolde" className="hover:text-[#00D4FF]">Truckwash Zeewolde</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold mb-5 tracking-wide">{t("footer.company")}</h4>
            <ul className="space-y-3 text-sm text-white/55">
              <li><Link to="/over-ons" className="hover:text-[#00D4FF]">{t("nav.about")}</Link></li>
              <li><Link to="/het-verhaal-achter-wasz" data-testid="footer-story" className="hover:text-[#00D4FF]">{lang === "nl" ? "Ons verhaal" : "Our story"}</Link></li>
              <li><Link to="/contact" className="hover:text-[#00D4FF]">{t("nav.contact")}</Link></li>
              <li><Link to="/blog" className="hover:text-[#00D4FF]">{t("nav.blog")}</Link></li>
              <li><Link to="/faq" className="hover:text-[#00D4FF]">{t("nav.faq")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-xs text-white/40">
          <div>© {year} {t("footer.copyright")}</div>
          <div className="flex items-center gap-6 flex-wrap">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#00D4FF] transition-colors">
              {lang === "nl" ? "Privacy" : "Privacy"}
            </Link>
            <Link to="/cookies" data-testid="footer-cookies" className="hover:text-[#00D4FF] transition-colors">
              {lang === "nl" ? "Cookies" : "Cookies"}
            </Link>
            <button
              data-testid="footer-cookie-settings"
              type="button"
              onClick={() => window.dispatchEvent(new Event("wasz:open-cookie-banner"))}
              className="hover:text-[#00D4FF] transition-colors"
            >
              {lang === "nl" ? "Cookie-instellingen" : "Cookie settings"}
            </button>
            <span>{lang === "nl" ? "KvK Almere" : "Chamber of Commerce Almere"}</span>
            <span>{lang === "nl" ? "BTW NL-richtlijn" : "Dutch VAT guideline"}</span>
            <span className="text-[#00D4FF]">v1.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
